import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getAllTickets, searchTickets } from "app/services/api/tickets";
import TicketDetailModal, {
  PrintTicketPrinterSelect,
} from "./components/TicketDetailModal";
import TicketSearchResultsGrid from "./components/TicketSearchResultsGrid";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";

const PAGE_SIZE_OPTIONS = [20, 40, 60, 80, 100];
const DEFAULT_PAGE_SIZE = 20;

const resolveTicketId = (ticket) => {
  const data = ticket?.ticket ?? ticket;
  return (
    data?.ticketId ??
    data?._id ??
    data?.id ??
    data?.ticket_id ??
    data?.ticket?._id ??
    null
  );
};

const AccessSystemPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const { getAuthUser } = useJumboAuth();
  const [input, setInput] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | loading | success | error
  const [items, setItems] = React.useState([]);
  const [meta, setMeta] = React.useState(null);
  const [query, setQuery] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = React.useState(null);

  const [listItems, setListItems] = React.useState([]);
  const [listTotal, setListTotal] = React.useState(0);
  const [listLoading, setListLoading] = React.useState(false);
  const [listError, setListError] = React.useState(null);
  const [listPage, setListPage] = React.useState(1);
  const [listLimit, setListLimit] = React.useState(DEFAULT_PAGE_SIZE);

  const topPaginationRef = React.useRef(null);
  const prevListPageRef = React.useRef(listPage);
  const searchInputRef = React.useRef(null);
  const scannerBufferRef = React.useRef("");
  const scannerLastKeyTimeRef = React.useRef(0);
  const SCANNER_KEY_THRESHOLD_MS = 100;

  const customer = getAuthUser?.()?.customer ?? null;

  const totalPages = Math.ceil(listTotal / listLimit) || 1;
  const showPagination = listTotal > listLimit;

  React.useEffect(() => {
    if (prevListPageRef.current !== listPage && listItems.length > 0) {
      prevListPageRef.current = listPage;
      topPaginationRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      prevListPageRef.current = listPage;
    }
  }, [listPage, listItems.length]);

  const searchActive = query !== null;

  const fetchAllTickets = React.useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const offset = (listPage - 1) * listLimit;
      const data = await getAllTickets({
        limit: listLimit,
        offset,
      });
      const rawItems = data?.items ?? data?.data ?? [];
      setListItems(Array.isArray(rawItems) ? rawItems : []);
      const total =
        data?.total ??
        data?.meta?.total ??
        data?.meta?.totalCount ??
        data?.pagination?.total ??
        data?.count ??
        (Array.isArray(rawItems) ? rawItems.length : 0);
      setListTotal(typeof total === "number" && total >= 0 ? total : 0);
    } catch (e) {
      setListError(e?.response?.data?.message ?? e?.message ?? String(e));
      setListItems([]);
      setListTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [listPage, listLimit]);

  React.useEffect(() => {
    if (!searchActive) {
      fetchAllTickets();
    }
  }, [searchActive, listPage, fetchAllTickets]);

  const handlePrintTicket = React.useCallback(
    (ticket) => {
      const ticketCode =
        ticket?.ticketCode ?? ticket?.code ?? ticket?.ticket_code ?? "";
      if (!ticketCode) return;
      showDialog({
        variant: "default",
        title: t(
          "pages.dsokDetails.accessSystem.detail.actions.print.selectPrinterTitle",
          { defaultValue: "Vyberte tlačiareň" },
        ),
        content: (
          <PrintTicketPrinterSelect
            customer={customer}
            customerId={customer?.id ?? customer?._id}
            ticketCode={ticketCode}
            onPrintSuccess={hideDialog}
            enqueueSnackbar={enqueueSnackbar}
          />
        ),
        actions: (
          <Button onClick={hideDialog}>
            {t("buttons.cancel", { defaultValue: "Zrušiť" })}
          </Button>
        ),
        maxWidth: "sm",
        fullWidth: true,
      });
    },
    [customer, enqueueSnackbar, hideDialog, showDialog, t],
  );

  const canSearch = input.length > 0 && status !== "loading";
  const canClear = input.length > 0 && status !== "loading";

  const handleClear = React.useCallback(() => {
    setInput("");
    setStatus("idle");
    setItems([]);
    setMeta(null);
    setQuery(null);
    setError(null);
    setSelectedTicket(null);
    setDetailOpen(false);
    setListPage(1);
  }, []);

  const handleSearch = React.useCallback(
    async (queryOverride) => {
      const raw = queryOverride != null ? String(queryOverride) : input;
      const q = String(raw ?? "").trim();
      if (!q) return;

      setStatus("loading");
      setItems([]);
      setMeta(null);
      setQuery(null);
      setError(null);
      setSelectedTicket(null);

      try {
        const data = await searchTickets({ q });
        setItems(Array.isArray(data?.items) ? data.items : []);
        setMeta(data?.meta ?? null);
        setQuery(data?.query ?? null);
        setStatus("success");
      } catch (e) {
        const httpStatus = e?.response?.status;
        setError({
          message: e?.response?.data?.message ?? e?.message ?? String(e),
          status: httpStatus,
        });
        setStatus("error");
      }
    },
    [input],
  );

  const handleRefresh = React.useCallback(() => {
    if (query !== null) {
      handleSearch();
    } else {
      fetchAllTickets();
    }
  }, [query, handleSearch, fetchAllTickets]);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      const el = document.activeElement;
      const isTyping =
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          (typeof el.isContentEditable === "boolean" && el.isContentEditable));
      if (isTyping) return;

      if (e.key === "Enter") {
        if (scannerBufferRef.current.length > 0) {
          const raw = scannerBufferRef.current;
          scannerBufferRef.current = "";
          setInput(raw);
          searchInputRef.current?.focus();
          handleSearch(raw);
          e.preventDefault();
          e.stopPropagation();
        } else {
          scannerBufferRef.current = "";
        }
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const now = Date.now();
        if (now - scannerLastKeyTimeRef.current > SCANNER_KEY_THRESHOLD_MS) {
          scannerBufferRef.current = "";
        }
        scannerBufferRef.current += e.key;
        scannerLastKeyTimeRef.current = now;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [handleSearch]);

  const selectedTicketId = React.useMemo(
    () => resolveTicketId(selectedTicket),
    [selectedTicket],
  );

  const handleSelectTicket = React.useCallback((ticket) => {
    setSelectedTicket(ticket);
    if (!resolveTicketId(ticket)) return;
    setDetailOpen(true);
  }, []);

  const pageTitle = t("sidebar.menuItem.accessSystemTickets", {
    defaultValue: t("pages.dsokDetails.accessSystem.title"),
  });

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "none" }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/access-system/tickets-management">
          {t("sidebar.menuItem.accessSystem")}
        </Link>
        <Typography color="text.primary">{pageTitle}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ mb: 3 }}>
        {pageTitle}
      </Typography>

      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
        spacing={1}
        flexWrap="wrap"
      >
        <Card sx={{ flex: 1, minWidth: 280, mb: 0, p: 2 }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSearch) handleSearch();
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                label={t("pages.dsokDetails.accessSystem.searchLabel")}
                helperText={t("pages.dsokDetails.accessSystem.searchHelper")}
                placeholder={t(
                  "pages.dsokDetails.accessSystem.searchPlaceholder",
                )}
                disabled={status === "loading"}
                autoComplete="off"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!canSearch}
                sx={{ minWidth: 140 }}
              >
                {status === "loading" ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} color="inherit" />
                    {t("pages.dsokDetails.accessSystem.loading")}
                  </Box>
                ) : (
                  t("pages.dsokDetails.accessSystem.search")
                )}
              </Button>
              <Button
                type="button"
                variant="outlined"
                disabled={!canClear}
                onClick={handleClear}
                sx={{ minWidth: 140 }}
              >
                {t("pages.dsokDetails.accessSystem.clear", {
                  defaultValue: "Vymazať",
                })}
              </Button>
              <Button
                type="button"
                variant="outlined"
                disabled={status === "loading" || listLoading}
                onClick={handleRefresh}
                sx={{ minWidth: 140 }}
              >
                {t("pages.dsokDetails.accessSystem.refresh", {
                  defaultValue: "Obnoviť",
                })}
              </Button>
            </Stack>
          </Box>
        </Card>

        {!searchActive && (
          <Box
            ref={topPaginationRef}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="tickets-page-size-label">
                {t("pages.dsokDetails.accessSystem.ticketsPerPage", {
                  defaultValue: "Počet",
                })}
              </InputLabel>
              <Select
                labelId="tickets-page-size-label"
                value={listLimit}
                label={t("pages.dsokDetails.accessSystem.ticketsPerPage", {
                  defaultValue: "Počet",
                })}
                onChange={(e) => {
                  const newLimit = Number(e.target.value) || DEFAULT_PAGE_SIZE;
                  setListLimit(newLimit);
                  setListPage(1);
                }}
                disabled={listLoading}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Pagination
              count={totalPages}
              page={listPage}
              onChange={(_, newPage) => setListPage(newPage)}
              color="primary"
              disabled={listLoading}
            />
          </Box>
        )}
      </Stack>

      <Card sx={{ p: 2 }}>
        {searchActive ? (
          <>
            {status === "success" && items.length === 0 ? (
              <Alert severity="info" variant="outlined">
                {t("pages.dsokDetails.accessSystem.emptyResults")}
              </Alert>
            ) : null}

            {status === "error" ? (
              <Alert severity="error" variant="outlined">
                {t("pages.dsokDetails.accessSystem.error")}
              </Alert>
            ) : null}

            {status === "success" && items.length >= 1 ? (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {t("pages.dsokDetails.accessSystem.resultsCount", {
                    value: meta?.count ?? items.length,
                  })}
                </Typography>
                {query?.matchedBy ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    {t("pages.dsokDetails.accessSystem.matchedBy.label", {
                      defaultValue: "Nájdené podľa",
                    })}
                    {": "}
                    {t(
                      `pages.dsokDetails.accessSystem.matchedBy.${String(
                        query.matchedBy,
                      )}`,
                      { defaultValue: String(query.matchedBy) },
                    )}
                  </Typography>
                ) : null}
                <TicketSearchResultsGrid
                  items={items}
                  onSelectTicket={handleSelectTicket}
                  onPrintTicket={handlePrintTicket}
                />
              </Box>
            ) : null}
          </>
        ) : (
          <>
            {listLoading && listItems.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={120}
              >
                <CircularProgress />
              </Box>
            ) : null}

            {listError ? (
              <Alert severity="error" variant="outlined">
                {listError}
              </Alert>
            ) : null}

            {!listLoading && listItems.length === 0 && !listError ? (
              <NoDataPlaceholder />
            ) : null}

            {listItems.length >= 1 ? (
              <Box>
                {listLoading ? (
                  <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                ) : null}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {t("pages.dsokDetails.accessSystem.resultsCount", {
                    value: listTotal,
                  })}
                </Typography>
                <TicketSearchResultsGrid
                  items={listItems}
                  onSelectTicket={handleSelectTicket}
                  onPrintTicket={handlePrintTicket}
                />
              </Box>
            ) : null}
          </>
        )}
      </Card>

      <TicketDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        ticketId={selectedTicketId}
        initialTicket={selectedTicket}
      />
    </Box>
  );
};

export default AccessSystemPage;
